import { useCallback, useEffect, useRef, useState } from "react";
import {
  privateIdentityKey,
  reducePrivateReveal,
  type PrivateRevealState,
} from "./private-surface";
/** Owner of the one private surface. `phaseId` is the reveal scope: the phase
 * for per-phase cards, `round:N` for a hand that holds for a round (Dice) or a
 * constant for a card that holds for the match (word, location, role). Every
 * other cover trigger is independent of the scope. */
export function usePrivatePanel({
  matchId,
  phaseId,
  conceal = false,
  roomInstanceId = "",
  playerId = null,
  presentation = "dialog",
}: {
  matchId: string | null;
  phaseId: string | null;
  conceal?: boolean;
  roomInstanceId?: string;
  playerId?: string | null;
  presentation?: "inline" | "dialog";
}) {
  const key = privateIdentityKey({
    roomInstanceId,
    playerId,
    matchId,
    phaseId,
  });
  const [state, setState] = useState<PrivateRevealState>(() => ({
    key,
    surface: null,
    presentation,
  }));
  // Scopes this seat has deliberately opened (presentation only: "look again"
  // hints). Never contains private values.
  const opened = useRef(new Set<string>());
  const current = reducePrivateReveal(state, {
    type: "sync",
    key,
    presentation,
    conceal,
  });
  // Guard during render: no stale private node can commit before an effect covers it.
  if (current !== state) setState(current);
  const cover = useCallback(
    () => setState((s) => reducePrivateReveal(s, { type: "cover" })),
    [],
  );
  useEffect(() => {
    const hide = () => {
      if (document.hidden) cover();
    };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("after-hours:cover", cover);
    return () => {
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("after-hours:cover", cover);
    };
  }, [cover]);
  return {
    open: current.surface !== null && !conceal,
    surface: current.surface,
    /** This seat has opened the private surface at least once in this scope. */
    opened: opened.current.has(key),
    reveal: () => {
      if (!conceal && !document.hidden) {
        opened.current.add(key);
        setState((s) =>
          reducePrivateReveal(s, {
            type: "reveal",
            key,
            surface: presentation,
          }),
        );
      }
    },
    cover,
  };
}
